import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "tavlacy_user_coords";
const MAX_AGE_MS = 1000 * 60 * 30; // 30 minutes

/**
 * Persistent user geolocation hook.
 *
 * Reads cached coords from localStorage on mount. `requestLocation()` kicks off
 * a fresh browser geolocation prompt and stores the result. `coords` is null
 * when we have no location (either never granted or stale).
 */
export function useUserLocation() {
  const [coords, setCoords] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | granted | denied | unsupported

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.lat && parsed?.lng && Date.now() - parsed.ts < MAX_AGE_MS) {
        setCoords({ lat: parsed.lat, lng: parsed.lng });
        setStatus("granted");
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      /* ignore malformed cache */
    }
  }, []);

  const requestLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!("geolocation" in navigator)) {
        setStatus("unsupported");
        reject(new Error("unsupported"));
        return;
      }
      setStatus("loading");
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ ...next, ts: Date.now() })
          );
          setCoords(next);
          setStatus("granted");
          resolve(next);
        },
        (err) => {
          setStatus("denied");
          reject(err);
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 }
      );
    });
  }, []);

  const clearLocation = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setCoords(null);
    setStatus("idle");
  }, []);

  return { coords, status, requestLocation, clearLocation };
}

/**
 * Haversine great-circle distance in kilometres.
 */
export function distanceKm(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some((v) => typeof v !== "number")) return null;
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * Human-readable distance string (i18n-aware via unit callback).
 * Returns "< 500 m" / "1.2 km" / "24 km".
 */
export function formatDistance(km, unitLabel = "km") {
  if (km == null) return "";
  if (km < 0.5) return `< 500 m`;
  if (km < 10) return `${km.toFixed(1)} ${unitLabel}`;
  return `${Math.round(km)} ${unitLabel}`;
}
